import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LANGUAGES, useLanguage } from '../contexts/LanguageContext';
import {
  ChevronLeft,
  ChevronRight,
  BarChart3,
  FileText,
  History,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  LogOut,
  Menu,
  Package,
  ShieldCheck,
  User,
  Users,
} from 'lucide-react';
import { canUseBasicIntelligence } from '../lib/insights';

export const SUPERADMIN_EMAIL = 'jerry.adithya@gmail.com';

const planLabels = {
  free: 'free',
  starter: 'starter',
  basic: 'Basic',
  pro: 'Pro',
  scale: 'Scale',
};

const planMessages = {
  free: ['free', 'upgrade'],
  starter: ['starter'],
  basic: ['Basic'],
  pro: ['Pro'],
  scale: ['Scale'],
};

const navItems = [
  { to: '/create', labelKey: 'createInvoice', icon: FileText },
  { to: '/dashboard', labelKey: 'dashboard', icon: LayoutDashboard, auth: true },
  { to: '/history', labelKey: 'history', icon: History, auth: true },
  { to: '/sales', labelKey: 'salesReports', icon: BarChart3, auth: true, basicOnly: true },
  { to: '/customers', labelKey: 'customers', icon: Users, auth: true },
  { to: '/items', labelKey: 'items', icon: Package, auth: true },
];

function Logo({ collapsed }) {
  const { t } = useLanguage();
  return (
    <Link to="/" className="flex items-center gap-2 min-w-0 group" data-testid="logo-link">
      <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center relative overflow-hidden shrink-0">
        <span className="absolute inset-0 bg-orange-500 translate-y-7 group-hover:translate-y-0 transition-transform duration-200" />
        <span className="relative font-heading font-black text-white text-lg leading-none">b</span>
      </div>
      {!collapsed && (
        <div className="leading-tight min-w-0">
          <div className="font-heading font-black text-slate-900 text-base tracking-tight truncate">billjaldi</div>
          <div className="flex items-center gap-1 text-[10px] tracking-[0.08em] uppercase text-slate-500 font-bold -mt-0.5 whitespace-nowrap">
            <span>{t('builtMadeIndia')}</span>
            <span className="text-red-500 text-[9px] leading-none">♥</span>
          </div>
        </div>
      )}
    </Link>
  );
}

function PlanBadge() {
  const { user, plan } = useAuth();
  const { t } = useLanguage();
  const [messageIndex, setMessageIndex] = useState(0);
  const normalizedPlan = plan || 'free';
  const messages = planMessages[normalizedPlan] || [planLabels[normalizedPlan] || `${normalizedPlan} plan`];
  const labelKey = messages[messageIndex % messages.length];
  const label = t(labelKey);
  const canUpgrade = normalizedPlan === 'free';

  useEffect(() => {
    setMessageIndex(0);
    if (messages.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setMessageIndex((i) => (i + 1) % messages.length);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [normalizedPlan, messages.length]);

  if (!user || user === false) return null;

  if (canUpgrade) {
    const upgradeState = labelKey === 'upgrade';
    return (
      <Link
        to="/plan"
        className={`group inline-flex w-28 items-center justify-center whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors hover:border-orange-500 hover:bg-orange-500 hover:text-white ${upgradeState ? 'border-orange-500 bg-orange-500 text-white' : 'border-orange-200 bg-orange-50 text-orange-700'}`}
        data-testid="current-plan-upgrade-link"
      >
        <span className="group-hover:hidden">{label}</span>
        <span className="hidden group-hover:inline">{t('upgrade')}</span>
      </Link>
    );
  }
  return (
    <Link
      to="/plan"
      className="inline-flex w-28 items-center justify-center whitespace-nowrap rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-orange-700 transition-colors hover:border-orange-500 hover:bg-orange-500 hover:text-white"
      data-testid="current-plan-link"
    >
      {label}
    </Link>
  );
}

function LanguageSelect() {
  const { language, setLanguage, t } = useLanguage();
  return (
    <label className="inline-flex items-center gap-2">
      <span className="sr-only">{t('language')}</span>
      <select
        value={language}
        onChange={(event) => setLanguage(event.target.value)}
        className="h-8 rounded-full border border-slate-200 bg-white px-3 text-xs font-black text-slate-600 shadow-sm outline-none transition-colors hover:border-orange-300 focus:border-orange-400"
        data-testid="language-select"
      >
        {LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>{lang.label}</option>
        ))}
      </select>
    </label>
  );
}

export function TopPlanBar({ collapsed, onToggle, onMobileMenu }) {
  const { t } = useLanguage();
  return (
    <header className={`fixed top-0 right-0 left-0 z-30 h-14 bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 transition-all duration-200 sm:h-16 sm:px-6 ${collapsed ? 'lg:left-16' : 'lg:left-64'}`}>
      <button
        type="button"
        onClick={onMobileMenu}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-900 shadow-sm lg:hidden"
        aria-label={t('expandSidebar')}
        data-testid="mobile-menu-toggle"
      >
        <Menu className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onToggle}
        className="hidden h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-900 shadow-sm lg:inline-flex"
        aria-label={collapsed ? t('expandSidebar') : t('collapseSidebar')}
        data-testid="sidebar-collapse-toggle"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
      <div className="flex items-center gap-2">
        <LanguageSelect />
        <PlanBadge />
      </div>
    </header>
  );
}

export default function Navbar({ collapsed, mobileOpen = false, onMobileClose = () => {} }) {
  const { user, plan, logout, businessProfile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutState, setLogoutState] = useState(null);
  const loggedIn = user && user !== false;
  const isSuperadmin = (user?.email || '').toLowerCase() === SUPERADMIN_EMAIL;
  const hasBasicIntelligence = canUseBasicIntelligence(plan);
  const sidebarName = businessProfile?.name || t('businessProfile');
  const visibleItems = navItems.filter((item) => !item.auth || loggedIn);
  const closeProfile = () => setProfileOpen(false);
  const doLogout = async () => {
    closeProfile();
    setLogoutState('signing-out');
    await logout();
    setLogoutState('done');
    window.setTimeout(() => {
      setLogoutState(null);
      navigate('/login', { replace: true });
    }, 850);
  };

  return (
    <>
    <aside className={`fixed inset-y-0 left-0 z-40 hidden bg-white border-r border-slate-200 shadow-sm flex-col transition-all duration-200 lg:flex ${collapsed ? 'w-16' : 'w-64'}`} data-testid="app-sidebar">
      <div className={`h-14 sm:h-16 flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
        <Logo collapsed={collapsed} />
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1">
        {visibleItems.map((item) => {
          const locked = item.basicOnly && !hasBasicIntelligence;
          const baseClass = `group relative flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-sm font-bold transition-colors ${collapsed ? 'justify-center' : ''}`;
          if (locked) {
            return (
              <button
                key={item.to}
                type="button"
                disabled
                aria-disabled="true"
                className={`${baseClass} w-full cursor-not-allowed text-slate-400`}
                title={collapsed ? t(item.labelKey) : undefined}
                data-testid={`sidebar-${item.labelKey}-locked`}
              >
                <span className="absolute inset-0 bg-slate-950/[0.035]" />
                <item.icon className="relative z-10 w-5 h-5 shrink-0" />
                {!collapsed && <span className="relative z-10 flex-1 text-left">{t(item.labelKey)}</span>}
                {!collapsed && <Lock className="relative z-10 h-3.5 w-3.5 shrink-0 text-slate-300" />}
                {collapsed && <Lock className="absolute bottom-1 right-1 z-10 h-3 w-3 text-slate-300" />}
              </button>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/create'}
              className={({ isActive }) =>
                `${baseClass} ${
                  isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                }`
              }
              title={collapsed ? t(item.labelKey) : undefined}
              data-testid={`sidebar-${item.labelKey}`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {!collapsed && <span>{t(item.labelKey)}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="relative border-t border-slate-200 p-2">
        <button
          type="button"
          onClick={() => setProfileOpen((v) => !v)}
          className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-100 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? t('profile') : undefined}
          aria-expanded={profileOpen}
          data-testid="profile-menu-trigger"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white shrink-0">
            <User className="w-4 h-4" />
          </span>
          {!collapsed && (
            <span className="min-w-0 text-left">
              <span className="block truncate">{loggedIn ? sidebarName : t('guest')}</span>
              <span className="block truncate text-[11px] font-semibold text-slate-400">{loggedIn ? user.email : t('notLoggedIn')}</span>
            </span>
          )}
        </button>

        {profileOpen && (
        <div className={`absolute bottom-3 ${collapsed ? 'left-14' : 'left-3 right-3'} z-50`}>
          <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl min-w-52">
            {loggedIn ? (
              <>
                <Link to="/settings" onClick={closeProfile} className="block rounded-lg px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">{t('profile')}</Link>
                <Link to="/help" onClick={closeProfile} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">
                  <LifeBuoy className="h-4 w-4" /> {t('help')}
                </Link>
                {isSuperadmin && <Link to="/admin" onClick={closeProfile} className="block rounded-lg px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">{t('adminPanel')}</Link>}
                <button onClick={doLogout} className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-bold text-slate-700 hover:bg-slate-100">
                  <LogOut className="w-4 h-4" /> {t('logout')}
                </button>
              </>
            ) : (
              <Link to="/login" onClick={closeProfile} className="block rounded-lg px-3 py-2 text-sm font-bold text-slate-900 hover:bg-slate-100">{t('login')}</Link>
            )}
          </div>
        </div>
        )}
      </div>
    </aside>
    {mobileOpen && (
      <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-sidebar">
        <button
          type="button"
          className="absolute inset-0 bg-slate-950/45"
          aria-label={t('collapseSidebar')}
          onClick={onMobileClose}
        />
        <aside className="relative flex h-full w-[min(84vw,320px)] flex-col border-r border-slate-200 bg-white shadow-2xl">
          <div className="flex h-14 items-center justify-between px-4">
            <Logo collapsed={false} />
            <button type="button" onClick={onMobileClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-900">
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {visibleItems.map((item) => {
              const locked = item.basicOnly && !hasBasicIntelligence;
              if (locked) {
                return (
                  <button
                    key={item.to}
                    type="button"
                    disabled
                    aria-disabled="true"
                    className="relative flex w-full cursor-not-allowed items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-sm font-bold text-slate-400"
                    data-testid={`mobile-sidebar-${item.labelKey}-locked`}
                  >
                    <span className="absolute inset-0 bg-slate-950/[0.035]" />
                    <item.icon className="relative z-10 h-5 w-5 shrink-0" />
                    <span className="relative z-10 flex-1 text-left">{t(item.labelKey)}</span>
                    <Lock className="relative z-10 h-3.5 w-3.5 shrink-0 text-slate-300" />
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/create'}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold transition-colors ${
                      isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                    }`
                  }
                  data-testid={`mobile-sidebar-${item.labelKey}`}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="border-t border-slate-200 p-3">
            {loggedIn ? (
              <div className="space-y-1">
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="truncate text-sm font-black text-slate-900">{sidebarName}</div>
                  <div className="truncate text-[11px] font-semibold text-slate-400">{user.email}</div>
                </div>
                <Link to="/settings" onClick={onMobileClose} className="block rounded-lg px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">{t('profile')}</Link>
                <Link to="/help" onClick={onMobileClose} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">
                  <LifeBuoy className="h-4 w-4" /> {t('help')}
                </Link>
                {isSuperadmin && <Link to="/admin" onClick={onMobileClose} className="block rounded-lg px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">{t('adminPanel')}</Link>}
                <button onClick={doLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-bold text-slate-700 hover:bg-slate-100">
                  <LogOut className="h-4 w-4" /> {t('logout')}
                </button>
              </div>
            ) : (
              <Link to="/login" onClick={onMobileClose} className="block rounded-lg px-3 py-2 text-sm font-bold text-slate-900 hover:bg-slate-100">{t('login')}</Link>
            )}
          </div>
        </aside>
      </div>
    )}
    {logoutState && (
      <div className="fixed inset-0 z-[100] bg-slate-950/45 backdrop-blur-sm flex items-center justify-center px-4" role="status" aria-live="polite" data-testid="logout-overlay">
        <div className="w-full max-w-sm rounded-2xl bg-white border border-slate-200 shadow-2xl p-7 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
            {logoutState === 'done' ? <ShieldCheck className="h-6 w-6" /> : <LogOut className="h-6 w-6 animate-pulse" />}
          </div>
          <div className="font-heading text-2xl font-black text-slate-900">
            {logoutState === 'done' ? t('loggedOut') : t('signingOut')}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {logoutState === 'done'
              ? t('logoutDoneCopy')
              : t('logoutProgressCopy')}
          </p>
        </div>
      </div>
    )}
    </>
  );
}
